import { resolveErrorMessage } from "@/screen/Identities/IdentityForm/identityErrorMessages";

export interface CompanyOnboardingErrorItem
{
    code: string;
    message: string;
}

// Mensajes de los errores propios del alta de empresas (representantes y borrador de empresa). Lo que no esté
// aquí se resuelve con la tabla del alta de intervinientes (red, reglas de datos...) o, en último caso, con el
// mensaje del backend, que para las reglas de datos ya es un texto para el usuario.
const CompanyOnboardingErrorMessages: Record<string, string> =
{
    // Representante
    ID_DOCUMENT_ALREADY_REGISTERED: "Esta persona ya está dada de alta entre tus intervinientes. No hace falta volver a darla de alta: podrás seleccionarla en el alta de la empresa.",
    ID_DOCUMENT_BELONGS_TO_OTHER_USER: "Esta persona ya está dada de alta en IronIA por otro usuario. Vuelve a leer su DNI para solicitar acceso a su titular.",
    LEGAL_REPRESENTATIVE_MINOR: "El representante de una empresa debe ser mayor de edad.",
    ID_DOCUMENT_INVALID: "El número del documento no es válido. Revisa que coincida con el DNI o NIE.",
    ID_TYPE_NOT_ALLOWED: "El representante debe darse de alta con DNI o NIE.",
    NATIONAL_COUNTRY_REQUIRED: "Indica la nacionalidad del representante.",
    FATCA_STATUS_REQUIRED: "Indica la situación FATCA del representante.",
    AML_HIGH_RISK: "No es posible dar de alta a esta persona. Ponte en contacto con nosotros para revisarlo.",
    IDENTITY_EXISTS_IN_OMNIA: "Esta persona ya es cliente de Diaphanum, pero todavía no está dada de alta en IronIA. Para poder seleccionarla como representante tenemos que darla de alta nosotros: escríbenos a info@ironia.tech indicando la empresa y te avisaremos cuando esté lista.",
    // Empresa
    TAX_ID_ALREADY_REGISTERED: "Esta empresa ya está dada de alta entre tus intervinientes.",
    TAX_ID_BELONGS_TO_OTHER_USER: "Esta empresa ya está dada de alta en IronIA por otro usuario. Ponte en contacto con nosotros.",
    TAX_ID_INVALID: "El CIF no es válido. Revisa la letra y el dígito de control.",
    INCORPORATION_DATE_IN_FUTURE: "La fecha de constitución no puede ser futura.",
    // Paso 3: datos, documentación y representantes
    COMPANY_NOT_FOUND: "No hemos encontrado la empresa. Vuelve a Gestión de Intervinientes y entra de nuevo.",
    COMPANY_NOT_EDITABLE: "Esta empresa ya se ha enviado y no se puede modificar.",
    REPRESENTATIVE_NOT_SELECTABLE: "Alguno de los representantes ya no está entre tus intervinientes. Vuelve a cargar la página y revisa la selección.",
    DOCUMENT_NOT_FOUND: "El documento ya no existe. Vuelve a cargar la página.",
    UPLOAD_ERROR: "No hemos podido subir el documento. Inténtalo de nuevo en unos minutos.",
    // Paso 4: envío a bastanteo
    COMPANY_EXISTS_IN_OMNIA: "Esta empresa ya es cliente de Diaphanum, pero todavía no está dada de alta en IronIA. Escríbenos a info@ironia.tech indicando la empresa y la daremos de alta nosotros.",
    DOCUMENT_READ_ERROR: "No hemos podido leer uno de los documentos de la empresa. Inténtalo de nuevo en unos minutos.",
    OMNIA_ERROR: "No hemos podido enviar la empresa a revisión. Hemos guardado todos los datos: inténtalo de nuevo en unos minutos.",
    // Errores técnicos: nunca se muestra el detalle del servidor
    PROCESS_ERROR: "No hemos podido completar la operación. Inténtalo de nuevo en unos minutos.",
};

// Familias de códigos de las integraciones (Omnia, Pibisi, NextPortfolio)
const NextPortfolioMessage = "Hemos guardado los datos, pero no hemos podido terminar el alta en nuestra plataforma de inversión. Vuelve a intentarlo más tarde con el mismo DNI y retomaremos el alta donde se quedó.";
const AmlMessage = "No hemos podido verificar los datos de esta persona. Revisa que sean correctos y, si el problema continúa, ponte en contacto con nosotros.";
const OmniaMessage = "No hemos podido conectar con nuestro sistema de clientes. Inténtalo de nuevo en unos minutos.";

function resolveCompanyOnboardingError(error: CompanyOnboardingErrorItem): string
{
    const code = (error.code ?? "").trim().toUpperCase();

    if(CompanyOnboardingErrorMessages[code])
    {
        return CompanyOnboardingErrorMessages[code];
    }
    if(code.startsWith("NEXTPORTFOLIO_") || code === "OMNIA_NEXTPORTFOLIO_SYNC_ERROR")
    {
        return NextPortfolioMessage;
    }
    if(code.startsWith("PIBISI_") || code.startsWith("AML_"))
    {
        return AmlMessage;
    }
    if(code.startsWith("OMNIA_"))
    {
        return OmniaMessage;
    }
    return resolveErrorMessage(code, error.message);
}

/**
 * Mensajes para el usuario a partir de los errores del backend: sin repetidos y sin detalles técnicos.
 * Si no hay ninguno utilizable se devuelve el mensaje genérico indicado.
 */
export function toCompanyOnboardingErrorMessages(errors: CompanyOnboardingErrorItem[] | null | undefined, fallback: string): string[]
{
    const messages = (errors ?? [])
        .map(resolveCompanyOnboardingError)
        .map((message) => (message ?? "").trim())
        .filter((message) => message !== "");

    const unique = Array.from(new Set(messages));
    return unique.length > 0 ? unique : [fallback];
}
